export interface BillingPlanMapping {
  priceId: string;
  plan: string;
}

export interface BillingPlanDisplay extends BillingPlanMapping {
  title: string;
  price: string;
  cadence: string;
  badge: string;
  description: string;
  cta: string;
  highlights: string[];
  kind: 'subscription' | 'add_on' | 'unknown';
  sortOrder: number;
}

const PLAN_DISPLAYS: Record<string, Omit<BillingPlanDisplay, keyof BillingPlanMapping>> = {
  solo: {
    title: 'Solo',
    price: '$79/mo',
    cadence: 'monthly subscription',
    badge: '1 included business line',
    description: 'For an owner/operator who wants to monitor one business line with Gmail and Calendar context.',
    cta: 'Subscribe to Solo',
    highlights: [
      'One user account included',
      'One business line included',
      'Gmail and Calendar context for that user',
      'Personalized coaching',
      'Mobile app access',
    ],
    kind: 'subscription',
    sortOrder: 10,
  },
  team: {
    title: 'Team',
    price: '$199/mo',
    cadence: 'monthly subscription',
    badge: '3 included business lines',
    description: 'For companies with multiple staff who need monitored business lines, team setup, and staff-specific context.',
    cta: 'Subscribe to Team',
    highlights: [
      'Three user accounts included',
      'Three business lines included',
      'Personalized coaching fine-tuned to each team member',
      'Gmail and Calendar context for each included user',
      'Mobile app access for the team',
    ],
    kind: 'subscription',
    sortOrder: 20,
  },
  business_line: {
    title: 'Extra business line',
    price: '$29/mo',
    cadence: 'per active monitored line',
    badge: 'Per active monitored line',
    description: 'Adds one more business number and line for additional team members beyond the three included with Team.',
    cta: 'Add Business Line',
    highlights: [
      'One additional user account',
      'One additional business line',
      'Assignable to a new team member',
      'Personalized coaching for that team member',
    ],
    kind: 'add_on',
    sortOrder: 30,
  },
};

function fallbackTitle(plan: string): string {
  return plan
    .split(/[_\-\s]+/)
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ') || 'Plan';
}

export function billingPlanDisplay(mapping: BillingPlanMapping): BillingPlanDisplay {
  const known = PLAN_DISPLAYS[mapping.plan];
  if (known) {
    return { ...mapping, ...known };
  }
  const title = fallbackTitle(mapping.plan);
  return {
    ...mapping,
    title,
    price: 'Configured in Stripe',
    cadence: 'monthly subscription',
    badge: 'Stripe checkout',
    description: 'This Stripe price is allow-listed on the server but does not have Oracord-specific display copy yet.',
    cta: `Subscribe to ${title}`,
    highlights: ['Checkout opens in Stripe', 'Server validates the price before redirecting'],
    kind: 'unknown',
    sortOrder: 100,
  };
}

export function billingPlanCards(mappings: BillingPlanMapping[]): BillingPlanDisplay[] {
  return mappings
    .map(billingPlanDisplay)
    .sort((a, b) => a.sortOrder - b.sortOrder || a.title.localeCompare(b.title));
}
